import { useEffect, useState, useRef } from "react";
import { getTree, getArboretum } from "./graph-structure";
import {
  getGlyphSlots,
  deleteTreeGlyphSlots,
  deleteTreeGlyphSlotsLast,
} from "../../requests/glyphslot";

const useTree = ({ defaultTree, treeId }) => {
  const [tree, setTree] = useState(defaultTree);
  const [glyphSlots, setGlyphSlots] = useState([]);
  const [loading, setLoading] = useState(true);

  const treeRef = useRef(null);
  const [dimensions, setDimensions] = useState({
    width: 0,
    height: 0,
  });

  const getTrees = (slots) => {
    const uniqueTreeIds = [...new Set(slots.map((s) => s.treeId))];
    const trees = uniqueTreeIds.map((id) => {
      const slotsForThis = slots.filter((s) => s.treeId === id);
      return getTree(id, slotsForThis);
    });
    return trees;
  };

  const respecTree = async () => {
    /*
      Handles cascading respecs.

      Basically when you respec, if you respec a root you will delete all orphaned children.
      If it is the final root make sure to not delete its center seed
    */
    setLoading(true);

    const wasRoot = tree.terminusActive;
    const allSlotsBeforeDelete = await getGlyphSlots({});
    const treesBeforeDelete = getTrees(allSlotsBeforeDelete);

    if (
      treesBeforeDelete.filter((t) => t.terminusActive)?.length <= 1 &&
      wasRoot
    ) {
      await deleteTreeGlyphSlotsLast({ treeId });
    } else {
      await deleteTreeGlyphSlots({ treeId });
    }

    if (wasRoot) {
      const allSlotsAfterDelete = await getGlyphSlots({});
      const trees = getTrees(allSlotsAfterDelete);
      const arboretumAfterDelete = getArboretum(trees);
      const disabledIds = arboretumAfterDelete.quadrants
        .filter((q) => q.disabled)
        .map((q) => q.id);
      for (const id of disabledIds) {
        if (allSlotsAfterDelete.some((s) => s.treeId === id)) {
          await deleteTreeGlyphSlots({ treeId: id });
        }
      }
    }

    const freshSlots = await getGlyphSlots({ treeId });
    setGlyphSlots(freshSlots);
    setLoading(false);
  };

  useEffect(() => {
    // Function to update dimensions
    const updateDimensions = () => {
      const update = { ...dimensions };
      if (treeRef.current) {
        const { offsetWidth, offsetHeight } = treeRef.current;
        update.width = offsetWidth;
        update.height = offsetHeight;
      }
      setDimensions(update);
    };

    // Update dimensions initially
    updateDimensions();
    // Remove any which existed already
    window.removeEventListener("resize", updateDimensions);
    // Add event listener to update dimensipons on window resize
    window.addEventListener("resize", updateDimensions);

    // Clean up the event listener on component unmount
    return () => {
      window.removeEventListener("resize", updateDimensions);
    };
  }, [tree]);

  useEffect(() => {
    setTree(getTree(treeId, glyphSlots));
  }, [glyphSlots]);

  useEffect(() => {
    getGlyphSlots({ treeId }).then((_glyphSlots) => {
      setGlyphSlots(_glyphSlots);
      setLoading(false);
    });
  }, []);

  return {
    tree,
    setTree,
    respecTree,
    treeRef,
    dimensions,
    loading,
    glyphSlots,
  };
};

export default useTree;
